import React from 'react';
import { Badge } from "../ui/badge";
import { 
  Paperclip, MessageSquare, Clock, 
  ArrowUp, ArrowDown, CircleAlert 
} from "lucide-react";
import { cn } from "../ui/utils";

import { 
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuLabel,
  DropdownMenuSeparator
} from "../ui/dropdown-menu";
import { Button } from "../ui/button";
import { MoreVertical, ArrowRightLeft } from "lucide-react";

export interface Ticket {
  id: string;
  formattedId: string;
  title: string;
  category: string;
  serviceDesk: string;
  status: string;
  priority: 'Urgent' | 'High' | 'Medium' | 'Low';
  submitter: { name: string; initials: string; color: string; email: string };
  assignees: { name: string; initials: string; color: string; email: string }[];
  createdAt: string;
  isBreached: boolean;
  comments: number;
  attachments: number;
  tags: string[];
}

interface TicketCardProps {
  ticket: Ticket;
  onDragStart?: (e: React.DragEvent, id: string) => void;
  onDragEnd?: () => void;
  isLaneHighlighted?: boolean;
  onClick?: () => void;
  canDrag?: boolean;
  statusConfig?: Record<string, { label: string }>;
  onMove?: (ticketId: string, newStatus: string) => void;
}

const PRIORITY_STYLES: Record<string, { label: string; color: string; icon: any }> = {
  Urgent: { label: "Urgent", color: "bg-red-100 text-red-700", icon: ArrowUp },
  High: { label: "High", color: "bg-orange-100 text-orange-600", icon: ArrowUp },
  Medium: { label: "Medium", color: "bg-blue-100 text-blue-700", icon: ArrowDown },
  Low: { label: "Low", color: "bg-gray-100 text-gray-600", icon: ArrowDown },
};

export const TicketCard: React.FC<TicketCardProps> = ({ 
  ticket, 
  onDragStart, 
  onDragEnd, 
  isLaneHighlighted,
  onClick,
  canDrag = true,
  statusConfig,
  onMove
}) => {
  const Priority = PRIORITY_STYLES[ticket.priority] || PRIORITY_STYLES.Medium;
  const PriorityIcon = Priority.icon;

  return (
    <div 
      draggable={canDrag && !!onDragStart}
      onDragStart={canDrag && onDragStart ? (e) => onDragStart(e, ticket.id) : undefined}
      onDragEnd={onDragEnd}
      onClick={onClick}
      className={cn(
        "bg-card border border-border rounded-lg p-4 transition-all mb-3 relative group",
        canDrag ? "cursor-grab active:cursor-grabbing hover:shadow-md" : "cursor-pointer",
        isLaneHighlighted && "border-gray-300 shadow-none"
      )}
    >
      {/* Header: Title + Priority */}
      <div className="flex items-start justify-between gap-2 mb-3">
        <h4 className="font-medium text-card-foreground line-clamp-2 leading-snug flex-1">
          {ticket.title}
        </h4>
        <span className={cn("inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-bold uppercase shrink-0", Priority.color)}>
          <PriorityIcon className="w-3 h-3" />
          {Priority.label}
        </span>
      </div>

      {/* Info: ID, Category, Submitter & Assignee */}
      <div className="space-y-3 mb-3">
        <div className="flex items-center gap-2 text-[13px] text-muted-foreground font-medium">
          <span>{ticket.formattedId}</span>
          <span>•</span>
          <span>{ticket.category}</span>
        </div>

        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className={cn("w-6 h-6 text-[10px] rounded-full flex items-center justify-center text-white font-bold", ticket.submitter.color)} title={ticket.submitter.name}>
              {ticket.submitter.initials}
            </div>
            <span className="text-sm text-muted-foreground font-medium">{ticket.submitter.name}</span>
          </div>

          <div className="flex items-center gap-1">
            <span className="text-[11px] text-muted-foreground font-bold uppercase tracking-tighter mr-1">Assigned:</span>
            <div className="flex -space-x-2 overflow-hidden">
                {ticket.assignees && ticket.assignees.length > 0 ? (
                    ticket.assignees.slice(0, 3).map((assignee, idx) => (
                        <div 
                            key={idx}
                            className={cn(
                                "w-6 h-6 text-[10px] rounded-full flex items-center justify-center text-white font-bold border-2 border-card", 
                                assignee.color
                            )} 
                            title={assignee.name}
                        >
                            {assignee.initials}
                        </div>
                    ))
                ) : (
                    <div className="w-6 h-6 rounded-full border border-dashed border-gray-300 flex items-center justify-center text-[10px] text-gray-400">
                        ?
                    </div>
                )}
                {ticket.assignees && ticket.assignees.length > 3 && (
                    <div className="w-6 h-6 rounded-full bg-gray-100 border-2 border-card flex items-center justify-center text-[8px] font-bold text-gray-500">
                        +{ticket.assignees.length - 3}
                    </div>
                )}
            </div>
          </div>
        </div>
      </div>

      {/* Footer: SLA + Stats + Mobile Action */}
      <div className="flex items-center justify-between pt-3 border-t border-border gap-2">
        {/* Left Side: SLA/Date */}
        {ticket.isBreached ? (
            <div className="flex items-center gap-1 px-2 py-0.5 bg-red-100 text-red-700 rounded border border-red-200 shrink-0">
              <CircleAlert className="w-3 h-3" />
              <span className="text-[10px] font-bold uppercase">Breached</span>
            </div>
        ) : (
            <div className="flex items-center gap-1 text-[11px] text-muted-foreground shrink-0">
                <Clock className="w-3 h-3" />
                <span>{new Date(ticket.createdAt).toLocaleDateString()}</span>
            </div>
        )}

        {/* Right Side: Stats & Mobile Action */}
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-1 text-xs text-muted-foreground font-medium">
              <Paperclip className="w-3.5 h-3.5" />
              <span>{ticket.attachments}</span>
            </div>
            <div className="flex items-center gap-1 text-xs text-muted-foreground font-medium">
              <MessageSquare className="w-3.5 h-3.5" />
              <span>{ticket.comments}</span>
            </div>
          </div>

          {/* Mobile Quick Move Menu - Moved to footer for prominence */}
          {onMove && statusConfig && (
            <div className="sm:hidden" onClick={(e) => e.stopPropagation()}>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="icon" className="h-8 w-8 rounded-full border-slate-200 bg-slate-50 text-slate-600 hover:text-slate-900 shadow-sm">
                    <ArrowRightLeft className="w-3.5 h-3.5" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48">
                  <DropdownMenuLabel className="text-[10px] font-black uppercase tracking-widest text-slate-400">Move to...</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  {Object.entries(statusConfig).map(([key, config]) => (
                    <DropdownMenuItem 
                      key={key} 
                      disabled={ticket.status === key}
                      onClick={() => onMove(ticket.id, key)}
                      className="gap-2 font-medium"
                    >
                      <div className={cn("w-2 h-2 rounded-full", ticket.status === key ? "bg-blue-500" : "bg-gray-300")} />
                      {config.label}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          )}
        </div>
      </div>

      {/* Tags */}
      {ticket.tags.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-3">
            {ticket.tags.map((tag) => (
              <span key={tag} className="px-1.5 py-0.5 bg-gray-100 text-gray-500 rounded text-[10px] uppercase font-bold tracking-tight">
                  {tag}
              </span>
            ))}
          </div>
      )}
    </div>
  );
};
